'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PhotoGalleryForm, type PhotoGalleryFormRef } from '@/components/partials/photo-galleries'
import { useCreatePhotoGallery } from '@/hooks'
import { type CreatePhotoGalleryParams } from '@/types/photo-gallery'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const photoGalleryFormRef = useRef<PhotoGalleryFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createPhotoGallery, isPending: isLoading } = useCreatePhotoGallery()

  const handleSubmit = (data: CreatePhotoGalleryParams) => {
    createPhotoGallery(data, {
      onSuccess: async newPhotoGallery => {
        setIsRedirecting(true)
        queryClient.setQueryData(['photo-gallery', newPhotoGallery.id], newPhotoGallery)
        await queryClient.invalidateQueries({ queryKey: ['photo-galleries'] })
        toast.success(t('created_successfully'))
        router.push(`/galleries/photo-galleries/${newPhotoGallery.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreatePhotoGalleryParams) => {
    createPhotoGallery(data, {
      onSuccess: async newPhotoGallery => {
        queryClient.setQueryData(['photo-gallery', newPhotoGallery.id], newPhotoGallery)
        await queryClient.invalidateQueries({ queryKey: ['photo-galleries'] })
        const detailUrl = `/galleries/photo-galleries/${newPhotoGallery.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        photoGalleryFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (photoGalleryFormRef.current) {
        photoGalleryFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/galleries/photo-galleries')
  }

  return (
    <PhotoGalleryForm
      key={formKey}
      ref={photoGalleryFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
